import { useMemo, useRef, useState } from 'react';
import useAudioContext from 'services/hooks/wavesurfer';
import { Song } from 'services/store/songs/types';
import { useSharedWaveformStyles } from 'services/hooks/waveform';
import Waveform from './components/Waveform';
import useStyles from './style';

type PropsType = {
  song: Song;
};

const SongWaveform = ({ song }: PropsType) => {
  const classes = useStyles();
  const sharedWaveformStyles = useSharedWaveformStyles();

  const {
    currentTime,
    isLoading,
    song: playerSong,
    seekTo,
    changeSong,
  } = useAudioContext();

  const isCurrentSong = useMemo(
    () => song.id === playerSong?.id,
    [song, playerSong]
  );

  const waveformDivRef = useRef(null);
  const [currentPos, setCurrentPos] = useState(0);

  const isSeeking = useRef(false);

  const currentPosition = useMemo(() => {
    if (isLoading) {
      return 0;
    }
    if (isSeeking.current) {
      return currentPos;
    }
    if (isCurrentSong) {
      return currentTime;
    }
    return 0;
  }, [currentTime, currentPos, isLoading, isCurrentSong]);

  const calcSeekedTime = (
    eMove: MouseEvent,
    waveformStart: number,
    waveformWidth: number
  ) => {
    const seekedPositionInPage = eMove.pageX;
    const clickPositionInBar = seekedPositionInPage - waveformStart;
    const timePerPixel = song.duration / waveformWidth;
    return timePerPixel * clickPositionInBar;
  };

  const handleTimeDrag = (e: MouseEvent) => {
    // to avoid selecting items on multiple click
    e.preventDefault();
    let seekedTime = 0;
    isSeeking.current = true;

    // get waveform dimensions
    const bar = waveformDivRef.current;
    // @ts-ignore
    const waveformStart = bar ? bar.getBoundingClientRect().left : 0;
    // @ts-ignore
    const waveformWidth = bar ? bar.offsetWidth : 0;

    // for normal click
    seekedTime = calcSeekedTime(e, waveformStart, waveformWidth);

    const updateTimeOnMove = (eMove: MouseEvent) => {
      seekedTime = calcSeekedTime(eMove, waveformStart, waveformWidth);
      seekedTime = seekedTime < 0 ? 0 : seekedTime;
      seekedTime = seekedTime > song.duration ? song.duration : seekedTime;
      setCurrentPos(seekedTime / 1000);
    };

    const onMouseButtonReleased = () => {
      const progress = seekedTime / song.duration;
      if (!isCurrentSong) {
        changeSong(song);
      }
      seekTo(progress);
      isSeeking.current = false;

      document.removeEventListener('mousemove', updateTimeOnMove);
      document.removeEventListener('mouseup', onMouseButtonReleased);
    };

    document.addEventListener('mousemove', updateTimeOnMove);
    document.addEventListener('mouseup', onMouseButtonReleased);
  };

  // TODO: Add Error handling
  if (song.peaks) {
    const { data } = song.peaks;

    return (
      <div
        role="button"
        aria-label="waveform"
        className={classes.waveformContainer}
        // @ts-ignore
        ref={waveformDivRef}
        data-testid="songWaveformSeekbar"
        tabIndex={0}
        // @ts-ignore
        onMouseDown={(e) => handleTimeDrag(e)}
      >
        <Waveform
          barWidth={sharedWaveformStyles.barWidth}
          peaks={data}
          pos={currentPosition * 1000}
          duration={song.duration}
          color={sharedWaveformStyles.waveColor}
          progressColor={sharedWaveformStyles.progressColor}
          transitionDuration={0}
        />
      </div>
    );
  }
  return null;
};

export default SongWaveform;
